<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import dayjs from "dayjs";
import { useApiClient } from "@/api/client";
import { toastApiResult } from "@/toaster";
import {
	maskInteger,
	unmaskInteger,
} from "@/modules/campaign_planner/utils/maskingUtils";
import FlightSummaryDTO = Modules.CampaignPlanner.Data.FlightSummaryDTO;
import FlightDO = Modules.CampaignPlanner.Data.FlightDO;

const props = defineProps<{
	sourceFlight: FlightSummaryDTO;
}>();

const emit = defineEmits(["cloned"]);

const isDialogVisible = defineModel<boolean>();

const flight = ref<FlightDO | null>(null);
const loading = ref(true);
const cloning = ref(false);

onMounted(async () => {
	const res = await useApiClient("campaignPlanner").getFlight(
		props.sourceFlight.uuid
	);
	if (res.isOk()) {
		const f = res.value;
		f.segments = [];
		f.opportunity = null;
		f.name = `CLONE: ${f.name ?? ""}`;
		flight.value = f;
	}
	loading.value = false;
});

const dateRange = computed({
	get() {
		if (!flight.value) return { start: null, end: null };
		return {
			start: flight.value.start_date ? new Date(flight.value.start_date) : null,
			end: flight.value.end_date ? new Date(flight.value.end_date) : null,
		};
	},
	set(value: { start: Date | null; end: Date | null }) {
		if (!flight.value) return;
		if (value?.start)
			flight.value.start_date = dayjs(value.start).format("YYYY-MM-DD");
		if (value?.end)
			flight.value.end_date = dayjs(value.end).format("YYYY-MM-DD");
	},
});

const goalTarget = computed({
	get() {
		if (!flight.value) return "";
		return maskInteger(flight.value.goal_target as number | null);
	},
	set(v: string) {
		if (flight.value) flight.value.goal_target = unmaskInteger(v);
	},
});

const showNonStandardAnalogStartDate = computed(
	() =>
		flight.value &&
		!flight.value.is_digital &&
		flight.value.start_date &&
		dayjs(flight.value.start_date).day() !== 0
);

const showNonStandardAnalogEndDate = computed(
	() =>
		flight.value &&
		!flight.value.is_digital &&
		flight.value.end_date &&
		dayjs(flight.value.end_date).day() !== 6
);

const showNonStandardDigitalStartDate = computed(
	() =>
		flight.value &&
		flight.value.is_digital &&
		flight.value.start_date &&
		dayjs(flight.value.start_date).day() !== 1
);

const showNonStandardDigitalEndDate = computed(
	() =>
		flight.value &&
		flight.value.is_digital &&
		flight.value.end_date &&
		dayjs(flight.value.end_date).day() !== 0
);

const weekdayItems = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const toTimeRules = [
	() => {
		const from = flight.value?.from_time_on_days;
		const to = flight.value?.to_time_on_days;
		if (from && to && to <= from) return "End time must be after start time";
		return true;
	},
];

const goalTargetRules = [
	() =>
		(flight.value?.goal_target != null &&
			(flight.value.goal_target as number) > 0) ||
		"Must be greater than 0",
];

const formRef = ref();

const clone = async () => {
	if (!flight.value) return;
	const { valid } = await formRef.value.validate();
	if (!valid) return;

	cloning.value = true;
	const res = await toastApiResult(
		useApiClient("campaignPlanner").cloneFlight(flight.value)
	);
	cloning.value = false;

	if (res.isOk()) {
		emit("cloned", res.value);
		isDialogVisible.value = false;
	}
};
</script>

<template>
	<v-dialog v-model="isDialogVisible" max-width="800" persistent scrollable>
		<v-card>
			<v-toolbar color="lightprimary" elevation="0" flat>
				<template #prepend>
					<v-icon class="ml-3">fa: fa-duotone fa-split</v-icon>
				</template>
				<v-toolbar-title>Clone flight</v-toolbar-title>
				<template #append>
					<v-btn icon variant="text" @click="isDialogVisible = false">
						<v-icon>fa: fa-regular fa-xmark</v-icon>
					</v-btn>
				</template>
			</v-toolbar>

			<v-divider />

			<v-card-text class="px-5 pb-0">
				<div v-if="loading" class="d-flex justify-center align-center py-10">
					<v-progress-circular indeterminate color="primary" />
				</div>

				<v-form v-else-if="flight" ref="formRef">
					<div class="section-content">
						<!-- Flight Goal -->
						<div class="field-group">
							<div class="field-label text-caption text-medium-emphasis">
								Flight Goal
							</div>
							<v-btn-toggle
								v-model="flight.goal_name_clean"
								mandatory
								density="compact"
								variant="outlined"
								color="primary"
							>
								<v-btn value="budget">Fixed Budget</v-btn>
								<v-btn value="reach">Fixed Impressions</v-btn>
							</v-btn-toggle>
						</div>

						<!-- Goal Target -->
						<div class="field-group">
							<div class="field-label text-caption text-medium-emphasis">
								{{
									flight.goal_name_clean === "budget"
										? "Budget (€)"
										: "Impressions"
								}}
							</div>
							<v-text-field
								v-model="goalTarget"
								type="text"
								inputmode="decimal"
								variant="filled"
								density="compact"
								hide-details="auto"
								:rules="goalTargetRules"
							/>
						</div>

						<!-- Period -->
						<v-fade-transition>
							<v-alert
								color="warning"
								:icon="false"
								v-if="
									showNonStandardAnalogStartDate ||
									showNonStandardAnalogEndDate ||
									showNonStandardDigitalStartDate ||
									showNonStandardDigitalEndDate
								"
							>
								<span style="font-size: 12px">
									This is a non-standard day to
									{{
										showNonStandardAnalogStartDate ||
										showNonStandardDigitalStartDate
											? "start"
											: "end"
									}}
									a '{{
										showNonStandardAnalogStartDate ||
										showNonStandardAnalogEndDate
											? "Analog"
											: "Digital"
									}}' campaign.
								</span>
							</v-alert>
						</v-fade-transition>
						<div class="field-group">
							<div class="field-label text-caption text-medium-emphasis">
								Period
							</div>
							<vc-date-picker
								v-model.range="dateRange"
								:columns="2"
								show-iso-weeknumbers
								mode="date"
								is-range
								:min-date="new Date()"
							>
								<template #default="{ inputValue, inputEvents }">
									<div class="d-flex gap-3 align-center">
										<v-text-field
											:value="inputValue.start"
											v-on="inputEvents.start"
											placeholder="Start"
											variant="filled"
											density="compact"
											hide-details
											readonly
										/>
										<v-icon class="mx-5" size="small" color="medium-emphasis"
											>fa: fa-light fa-arrow-right</v-icon
										>
										<v-text-field
											:value="inputValue.end"
											v-on="inputEvents.end"
											placeholder="End"
											variant="filled"
											density="compact"
											hide-details
											readonly
										/>
									</div>
								</template>
							</vc-date-picker>
						</div>

						<!-- Time & Weekdays (digital only) -->
						<template v-if="flight.is_digital">
							<div class="field-group">
								<div class="field-label text-caption text-medium-emphasis">
									Time &amp; Weekdays
								</div>
								<div class="d-flex gap-3 align-center">
									<v-text-field
										v-model="flight.from_time_on_days"
										placeholder="From"
										variant="filled"
										density="compact"
										hide-details
										style="max-width: 150px"
									/>
									<v-icon class="mx-5" size="small" color="medium-emphasis"
										>fa: fa-light fa-arrow-right</v-icon
									>
									<v-text-field
										v-model="flight.to_time_on_days"
										placeholder="To"
										variant="filled"
										density="compact"
										hide-details="auto"
										:rules="toTimeRules"
										style="max-width: 150px"
									/>
									<v-select
										class="ml-10"
										v-model="flight.weekdays"
										:items="weekdayItems"
										placeholder="Weekdays"
										variant="filled"
										density="compact"
										hide-details
										multiple
									/>
								</div>
							</div>
						</template>

						<!-- Prioritize unused screens -->
						<div class="field-group field-group--inline">
							<div
								class="field-label text-caption text-medium-emphasis"
								v-if="flight.is_vendor_fit_enabled"
							>
								Prioritize screens not yet used in original flight
							</div>
							<div class="field-label text-caption text-medium-emphasis" v-else>
								Exclude screens used in original flight
							</div>
							<v-switch
								v-model="flight.is_screen_reuse_strategy_enabled"
								color="primary"
								density="compact"
								hide-details
								class="mt-0"
							/>
						</div>
					</div>
				</v-form>
			</v-card-text>

			<v-divider />

			<v-card-actions>
				<v-spacer />
				<v-btn variant="text" @click="isDialogVisible = false">Cancel</v-btn>
				<v-btn
					variant="tonal"
					color="primary"
					:loading="cloning"
					:disabled="loading || !flight"
					@click="clone"
					>Clone</v-btn
				>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<style scoped lang="scss">
.section-content {
	display: flex;
	flex-direction: column;
	gap: 20px;
	padding: 4px 0 8px;
}

.field-group {
	display: flex;
	flex-direction: column;
	gap: 8px;

	&--inline {
		flex-direction: row;
		align-items: center;
		justify-content: space-between;
		margin-right: 8px;
	}
}

.field-label {
	font-size: 0.75rem;
	letter-spacing: 0.03em;
	text-transform: uppercase;
}

:deep(.v-btn-group .v-btn) {
	font-weight: 400;
	text-transform: none;
	letter-spacing: normal;
}
</style>
